import { useEffect, useRef } from "react";
import { Sparkles, MessageCircle, Highlighter, StickyNote, X } from "lucide-react";
import type { PdfSelection } from "./types";

type Props = {
  selection: PdfSelection;
  onExplain: () => void;
  onAsk: () => void;
  onNote: () => void;
  onHighlight: () => void;
  onClose: () => void;
};

export function PdfFloatingMenu({
  selection,
  onExplain,
  onAsk,
  onNote,
  onHighlight,
  onClose,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const r = selection.anchorClientRect;
    const menuHeight = 44;
    const menuWidth = 220;
    
    // Position above selection by default
    let top = r.top - menuHeight - 8;
    // If not enough space above, position below
    if (top < 8) {
      top = r.bottom + 8;
    }
    
    let left = r.left + (r.width / 2) - (menuWidth / 2);
    // Keep within viewport
    left = Math.min(window.innerWidth - menuWidth - 8, Math.max(8, left));

    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
  }, [selection]);

  return (
    <div
      ref={ref}
      className="fixed z-50 flex items-center gap-1 rounded-full border bg-background/95 px-2 py-1.5 shadow-lg backdrop-blur"
      role="toolbar"
      aria-label="PDF selection actions"
    >
      <button
        className="rounded-full p-2 text-premium hover:bg-premium/10 transition-colors"
        title="Explain"
        onClick={onExplain}
      >
        <Sparkles className="h-4 w-4" />
      </button>

      <button
        className="rounded-full p-2 text-teal hover:bg-teal/10 transition-colors"
        title="Ask"
        onClick={onAsk}
      >
        <MessageCircle className="h-4 w-4" />
      </button>

      <button
        className="rounded-full p-2 text-info hover:bg-info/10 transition-colors"
        title="Note"
        onClick={onNote}
      >
        <StickyNote className="h-4 w-4" />
      </button>

      <button
        className="rounded-full p-2 text-warning hover:bg-warning/10 transition-colors"
        title="Highlight"
        onClick={onHighlight}
      >
        <Highlighter className="h-4 w-4" />
      </button>

      <div className="mx-1 h-4 w-px bg-border" />

      <button
        className="rounded-full p-2 text-muted-foreground hover:bg-muted transition-colors"
        title="Close"
        onClick={onClose}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
